"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useForm, type UseFormReturn } from "react-hook-form";
import { scrollToFirstError } from "@west-tennessee-consulting/ui";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { AlertCircle } from "lucide-react";
import { TitleAndTypeFields } from "./communication/TitleAndTypeFields";
import { DirectionAndStatusFields } from "./communication/DirectionAndStatusFields";
import { DateTimeAndExternalFields } from "./communication/DateTimeAndExternalFields";
import { FollowUpSection } from "./communication/FollowUpSection";
import { ContactSelector } from "./communication/components/ContactSelector";
import { CommunicationDetails } from "./communication/components/CommunicationDetails";
import {
  communicationSchema,
  COMMUNICATION_DIRECTION_SELECT_OPTIONS,
  COMMUNICATION_STATUS_SELECT_OPTIONS,
} from "./communication/communicationFormSchema";
import type {
  CommunicationFormValues,
  CommunicationFormSubmission,
  CommunicationFormProps,
} from "./communication/communicationFormSchema";
import { useFollowUpSuggestion } from "./communication/useFollowUpSuggestion";
import { FormSectionGroup } from "@/components/shared/forms/FormSectionGroup";
import { defineTaxonomySection } from "@/components/shared/forms/FormSectionGroup/section-taxonomy";

export type { CommunicationFormSubmission };

import { useCommunicationTypes } from "@/lib/hooks/api/communications/communication-types";

export const CommunicationForm: React.FC<CommunicationFormProps> = ({
  contactId,
  userId,
  onSubmit,
  isSubmitting,
  typeOptions: passedTypeOptions,
  isLoadingTypes: passedIsLoadingTypes,
}) => {
  const [error, setError] = useState<string | null>(null);

  const form = useForm<CommunicationFormValues>({
    resolver: zodResolver(communicationSchema),
    defaultValues: useMemo(
      () => ({
        title: "",
        body: "",
        occurred_at: new Date(),
        direction: "outbound",
        status: "completed",
        contact_id: contactId || undefined,
        follow_up_schedule: false,
        follow_up_note: "",
      }),
      [contactId]
    ),
  });

  const bodyValue = form.watch("body");
  const showFollowUp = form.watch("follow_up_schedule");

  const { handleFollowUpToggle, isSuggesting } = useFollowUpSuggestion(form, bodyValue);

  // Use passed options or fetch communication types as fallback
  const {
    options: internalTypeOptions,
    loading: internalIsLoadingTypes,
    error: typesError,
  } = useCommunicationTypes();

  // Use passed options if available, otherwise use internal data
  const typeOptions = useMemo(() => {
    if (passedTypeOptions && passedTypeOptions.length > 0) {
      return passedTypeOptions;
    }
    return internalTypeOptions || [];
  }, [passedTypeOptions, internalTypeOptions]);

  const isLoadingTypes = passedIsLoadingTypes ?? internalIsLoadingTypes;

  const handleSubmit = useCallback(
    async (values: CommunicationFormValues) => {
      setError(null);
      try {
        // Require a contact: either from props or selection
        const selectedContactId = contactId || values.contact_id;
        if (!selectedContactId) {
          setError("Please select a contact.");
          return;
        }

        // Pass through with normalized contact_id/user_id for GraphQL mutation
        const payload: CommunicationFormSubmission = {
          title: values.title,
          type_id: values.type_id,
          body: values.body,
          direction: values.direction,
          status: values.status,
          external_id: values.external_id,
          metadata: values.metadata,
          occurred_at: values.occurred_at?.toISOString(),
          contact_id: selectedContactId,
          user_id: userId,
          follow_up_schedule: values.follow_up_schedule,
          follow_up_due_date: values.follow_up_due_date,
          follow_up_note: values.follow_up_note,
        };

        await onSubmit(payload);
        form.reset();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to log communication");
      }
    },
    [contactId, userId, onSubmit, form]
  );

  // Progressive-disclosure section layout (plan
  // `the-log-activity-modal-enumerated-whisper.md`).
  //
  // Recomputed when toggleable inputs change so collapsibles can react
  // (FollowUpSection's content also reflects the toggle). `FormSectionGroup`
  // captures `initialOpen` once on mount, so changing `defaultOpen`
  // post-mount doesn't snap sections open/closed against the user.
  const sections = useMemo(
    () => [
      defineTaxonomySection<CommunicationFormValues>("core", {
        title: "Core details",
        summary: "Title, type, direction, and details",
        kind: "always",
        render: (f) => (
          <div className="space-y-4">
            <TitleAndTypeFields
              control={f.control}
              typeOptions={typeOptions}
              isLoadingTypes={isLoadingTypes}
              isSubmitting={isSubmitting}
            />
            <DirectionAndStatusFields
              control={f.control}
              directionOptions={COMMUNICATION_DIRECTION_SELECT_OPTIONS}
              statusOptions={COMMUNICATION_STATUS_SELECT_OPTIONS}
              isSubmitting={isSubmitting}
            />
            <CommunicationDetails control={f.control} isSubmitting={isSubmitting} />
          </div>
        ),
      }),
      defineTaxonomySection<CommunicationFormValues>("scheduling", {
        summary: "When this happened and any external reference",
        hasValue: (f) => Boolean(f.watch("external_id")),
        render: (f) => (
          <DateTimeAndExternalFields control={f.control} isSubmitting={isSubmitting} />
        ),
      }),
      defineTaxonomySection<CommunicationFormValues>("follow-up", {
        summary: "Schedule a reminder for next steps",
        defaultOpen: Boolean(showFollowUp),
        hasValue: (f) => Boolean(f.watch("follow_up_schedule")),
        render: (f) => (
          <FollowUpSection
            control={f.control}
            showFollowUp={Boolean(f.watch("follow_up_schedule"))}
            onToggle={handleFollowUpToggle}
            isSuggesting={isSuggesting}
            isSubmitting={isSubmitting}
          />
        ),
      }),
    ],
    // `showFollowUp` only affects defaultOpen on mount; intentionally not in
    // deps to avoid re-creating the descriptor array per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeOptions, isLoadingTypes, isSubmitting, handleFollowUpToggle, isSuggesting]
  );

  if (typesError) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg dark:bg-red-950/40 dark:text-red-300">
        <AlertCircle size={16} />
        <span className="text-sm">Failed to load communication types</span>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)} className="space-y-4">
        {/* Contact Selector (required when not provided by parent) */}
        {!contactId && <ContactSelector control={form.control} isSubmitting={isSubmitting} />}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg dark:bg-red-950/40 dark:text-red-300">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <FormSectionGroup
          form={form as UseFormReturn<CommunicationFormValues>}
          sections={sections}
        />

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Logging...</span>
              </>
            ) : (
              "Log Communication"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
